import { Input } from '@/components/ui/FormControls';
import BrandLogo from '@/components/common/BrandLogo';
import { brandConfig } from '@/config/brandConfig';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, ShieldCheck, LogOut, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { temporaryPasswordSchema, accountEmailSchema } from '@klothiq/validation';
import { manageLogin } from '@/lib/user-admin';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export default function AccountSetupPage() {
  const { access, accessError, profile, user, refreshProfile, signOut } = useAuth();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [newEmail, setNewEmail] = useState('');
  const pending = useQuery({
    queryKey: ['requested_email', access?.profile_id, access?.access_version],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_requested_email');
      if (error) throw error;
      return data as { email: string; expires_at: string } | null;
    },
    enabled: !!access?.ready,
  });
  const setupAllowed =
    !!access?.session_valid &&
    !access.onboarding_expired &&
    ['active', 'onboarding'].includes(access.status) &&
    (access.ready || access.must_change_password);
  const savePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    try {
      temporaryPasswordSchema.parse(password);
      if (password !== confirmation) throw new Error('The passwords do not match.');
      if (password === current)
        throw new Error('Choose a new password different from your current password.');
      setBusy(true);
      await manageLogin({
        action: 'complete_password',
        current_password: current,
        new_password: password,
        request_key: key,
      });
      setCurrent('');
      setPassword('');
      setConfirmation('');
      toast.success('Password updated. Sign in with your new password.');
      await signOut();
    } catch (error: any) {
      setError(error.issues?.[0]?.message ?? error.message);
    } finally {
      setBusy(false);
    }
  };
  const acceptEmail = async () => {
    if (!current) {
      setError('Enter your current password before confirming the email change.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await manageLogin({
        action: 'accept_email',
        current_password: current,
        request_key: crypto.randomUUID(),
      });
      setCurrent('');
      toast.success(
        'Check the required email verification links. Your current login stays available until confirmation.',
      );
    } catch (error: any) {
      setError(error.message);
    } finally {
      setBusy(false);
    }
  };
  const requestOwnEmail = async () => {
    setError('');
    try {
      const email = accountEmailSchema.parse(newEmail);
      if (!current) throw new Error('Enter your current password to confirm the request.');
      setBusy(true);
      await manageLogin({
        action: 'request_email',
        profile_id: access!.profile_id,
        version: access!.profile_version,
        email,
        reason: 'Super Admin requested own login email correction',
        current_password: current,
        request_key: crypto.randomUUID(),
      });
      setNewEmail('');
      setCurrent('');
      await refreshProfile();
      await pending.refetch();
      toast.success('Email change requested. Approve it below to request verification.');
    } catch (error: any) {
      setError(error.issues?.[0]?.message ?? error.message);
    } finally {
      setBusy(false);
    }
  };
  const inputClass =
    'mt-2 min-h-11 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary';
  return (
    <main className="flex min-h-[100dvh] items-center justify-center bg-background p-4 sm:p-8">
      <section className="w-full max-w-md space-y-5 rounded-3xl border border-border bg-card p-6 shadow-xl sm:p-8">
        <div className="flex items-center gap-3">
          <BrandLogo className="h-12 w-12 rounded-2xl" width={48} height={48} />
          <div className="min-w-0 flex-1">
            <p className="font-bold">{brandConfig.name}</p>
            <p className="text-xs text-muted-foreground">{brandConfig.tagline}</p>
          </div>
          <ShieldCheck className="h-6 w-6 shrink-0 text-primary" />
        </div>
        <header>
          <h1 className="text-xl font-bold">
            {access?.must_change_password && setupAllowed
              ? 'Set your personal password'
              : access?.ready
                ? 'My Account'
                : 'Account access unavailable'}
          </h1>
          <p className="mt-2 break-all text-sm text-muted-foreground">
            {profile?.full_name ?? 'KLOTHIQ'}
            {user?.email ? ` · ${user.email}` : ''}
          </p>
        </header>
        {!setupAllowed ? (
          <>
            <p role="alert" className="text-sm text-muted-foreground">
              {accessError ??
                (access?.onboarding_expired
                  ? 'Your temporary login expired. Ask your Super Admin for a new temporary password.'
                  : !access?.session_valid
                    ? 'Please sign out and sign in again to continue.'
                    : 'This login is not active. Contact your Super Admin.')}
            </p>
            <button
              className="min-h-11 w-full rounded-xl border border-border text-sm"
              onClick={() => refreshProfile()}
            >
              Try again
            </button>
          </>
        ) : (
          <>
            {access?.must_change_password && (
              <p className="rounded-xl bg-primary/5 p-3 text-sm text-muted-foreground">
                Use the temporary password shared by your Super Admin, then choose a password only
                you know.
              </p>
            )}
            {pending.data && (
              <div className="space-y-3 rounded-xl border border-primary/30 p-4">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <Mail className="h-4 w-4 text-primary" /> Email change requested
                </h2>
                <p className="break-all text-sm">{pending.data.email}</p>
                <p className="text-xs text-muted-foreground">
                  Your current email keeps working until the required verification links are
                  confirmed.
                </p>
                <button
                  className="min-h-11 w-full rounded-xl border border-border px-3 text-sm"
                  disabled={busy}
                  onClick={acceptEmail}
                >
                  Approve and request verification
                </button>
              </div>
            )}
            <form onSubmit={savePassword} className="space-y-4">
              <label className="block text-sm font-medium">
                {access?.must_change_password ? 'Temporary password' : 'Current password'}
                <Input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  className={inputClass}
                  helpLabel="Current password"
                />
              </label>
              <label className="block text-sm font-medium">
                New password
                <Input
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  maxLength={64}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setKey(crypto.randomUUID());
                  }}
                  className={inputClass}
                  helpLabel="New password Use 12–64 characters."
                />
                <span className="mt-1 block text-xs font-normal text-muted-foreground">
                  Use 12–64 characters.
                </span>
              </label>
              <label className="block text-sm font-medium">
                Confirm new password
                <Input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  className={inputClass}
                  helpLabel="Confirm new password"
                />
              </label>
              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={busy}
                className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                <KeyRound className="h-4 w-4" />
                {busy ? 'Updating…' : 'Save new password'}
              </button>
            </form>
          </>
        )}
        {access?.ready && access.role === 'owner' && (
          <section className="space-y-3 border-t border-border pt-4">
            <h2 className="text-sm font-semibold">Change my login email</h2>
            <label className="block text-sm">
              New email
              <Input
                type="email"
                className={inputClass}
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
                helpLabel="New email"
              />
            </label>
            <p className="text-xs text-muted-foreground">
              Enter your current password above. Your current login stays available until
              verification.
            </p>
            <button
              disabled={busy || !newEmail}
              className="min-h-11 w-full rounded-xl border border-border px-3 text-sm disabled:opacity-50"
              onClick={requestOwnEmail}
            >
              Request email change
            </button>
          </section>
        )}
        <button
          disabled={busy}
          className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-border text-sm disabled:opacity-50"
          onClick={async () => {
            try {
              await signOut();
            } catch {
              setError('Unable to sign out. Try again.');
            }
          }}
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </section>
    </main>
  );
}
